import { useEffect, useMemo, useRef, useState } from 'react'
import { cx } from './ui'
import { useT } from '../lib/i18n'

export interface SearchItem {
  id: string
  label: string
  sub?: string
  /** optional hierarchy the item sits in, e.g. [category, subCategory]; enables browsing */
  path?: string[]
}

type Row = { kind: 'group'; name: string; count: number } | { kind: 'item'; item: SearchItem }

/**
 * A searchable single-select over a preloaded list. Items with a `path` can also be browsed:
 * with an empty query the list shows the next level of groups, and typing searches within the
 * current level. Backspace on an empty query goes up one level.
 */
export function Search({
  items,
  value,
  onChange,
  placeholder,
  autoFocus,
}: {
  items: SearchItem[]
  value: string | null
  onChange: (id: string | null) => void
  placeholder?: string
  autoFocus?: boolean
}) {
  const { t } = useT()
  const selected = useMemo(() => items.find((i) => i.id === value) ?? null, [items, value])
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [hi, setHi] = useState(0)
  const [crumbs, setCrumbs] = useState<string[]>([])
  const box = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const hierarchical = useMemo(() => items.some((i) => i.path && i.path.length > 0), [items])

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const rows = useMemo<Row[]>(() => {
    const s = q.trim().toLowerCase()
    const inScope = items.filter((i) => crumbs.every((c, k) => (i.path ?? [])[k] === c))
    if (s) {
      return inScope
        .filter((i) => i.label.toLowerCase().includes(s) || i.id.toLowerCase().includes(s) || (i.sub ?? '').toLowerCase().includes(s) || (i.path ?? []).some((p) => p.toLowerCase().includes(s)))
        .slice(0, 40)
        .map((item) => ({ kind: 'item', item }))
    }
    if (!hierarchical) return inScope.slice(0, 40).map((item) => ({ kind: 'item', item }))
    const depth = crumbs.length
    const groups = new Map<string, number>()
    const leaves: SearchItem[] = []
    for (const i of inScope) {
      const next = (i.path ?? [])[depth]
      if (next) groups.set(next, (groups.get(next) ?? 0) + 1)
      else leaves.push(i)
    }
    const out: Row[] = [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([name, count]) => ({ kind: 'group', name, count }))
    for (const item of leaves.sort((a, b) => a.label.localeCompare(b.label))) out.push({ kind: 'item', item })
    return out
  }, [items, q, crumbs, hierarchical])

  useEffect(() => {
    const el = list.current?.children[hi] as HTMLElement | undefined
    el?.scrollIntoView?.({ block: 'nearest' })
  }, [hi])

  const pick = (row: Row) => {
    if (row.kind === 'group') {
      setCrumbs((c) => [...c, row.name])
      setQ('')
      setHi(0)
      return
    }
    onChange(row.item.id)
    setOpen(false)
  }

  const openAt = () => {
    setOpen(true)
    setQ('')
    setHi(0)
    setCrumbs(selected?.path ?? [])
  }

  return (
    <div ref={box} className="relative">
      <input
        className="field"
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={open ? q : selected ? `${selected.label}` : q}
        onFocus={openAt}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
          setHi(0)
        }}
        onKeyDown={(e) => {
          if (!open) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHi((h) => Math.min(h + 1, rows.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHi((h) => Math.max(h - 1, 0))
          } else if (e.key === 'Enter' || e.key === 'Return' || e.keyCode === 13) {
            e.preventDefault()
            const r = rows[hi]
            if (r) pick(r)
          } else if (e.key === 'Backspace' && q === '' && crumbs.length > 0) {
            e.preventDefault()
            setCrumbs((c) => c.slice(0, -1))
            setHi(0)
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
      />
      {selected && !open && <div className="pointer-events-none absolute right-2.5 top-1.5 text-xs text-slate-500">{selected.sub ?? selected.id}</div>}
      {open && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-slate-700 bg-slate-900 shadow-lg">
          {hierarchical && (
            <div className="flex flex-wrap items-center gap-1 border-b border-slate-800 px-2.5 py-1.5 text-xs">
              <button type="button" className={cx('rounded px-1 py-0.5 hover:bg-slate-800', crumbs.length === 0 ? 'font-semibold text-slate-100' : 'text-slate-400')} onMouseDown={(e) => e.preventDefault()} onClick={() => { setCrumbs([]); setHi(0) }}>
                {t('All')}
              </button>
              {crumbs.map((c, k) => (
                <span key={k} className="flex items-center gap-1">
                  <span className="text-slate-600">›</span>
                  <button
                    type="button"
                    className={cx('rounded px-1 py-0.5 hover:bg-slate-800', k === crumbs.length - 1 ? 'font-semibold text-slate-100' : 'text-slate-400')}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setCrumbs(crumbs.slice(0, k + 1)); setHi(0) }}
                  >
                    {c}
                  </button>
                </span>
              ))}
              {q.trim() && <span className="ml-auto text-slate-500">{crumbs.length ? t('searching here') : t('searching everywhere')}</span>}
            </div>
          )}
          <div ref={list} className="max-h-72 overflow-auto">
            {rows.length === 0 && <div className="px-2.5 py-2 text-xs text-slate-500">{t('No matches')}</div>}
            {rows.map((r, i) =>
              r.kind === 'group' ? (
                <button
                  type="button"
                  key={`g:${r.name}`}
                  onMouseEnter={() => setHi(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(r)}
                  className={cx('flex w-full items-center justify-between gap-3 px-2.5 py-1.5 text-left text-sm', i === hi ? 'bg-slate-800/60 text-slate-100' : 'text-slate-200')}
                >
                  <span className="font-medium">{r.name}</span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-slate-500">
                    {r.count}
                    <span className="text-slate-600">›</span>
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  key={r.item.id}
                  onMouseEnter={() => setHi(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(r)}
                  className={cx('flex w-full items-baseline justify-between gap-3 px-2.5 py-1.5 text-left text-sm', i === hi ? 'bg-slate-800/60 text-slate-100' : 'text-slate-300')}
                >
                  <span>{r.item.label}</span>
                  <span className="shrink-0 text-xs text-slate-500">{q.trim() && r.item.path?.length ? `${r.item.sub ?? r.item.id} · ${r.item.path.join(' › ')}` : r.item.sub ?? r.item.id}</span>
                </button>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  )
}
